// Countertops/Cabinets manual estimate — persistence payload <-> frontend model mapping.
//
// The estimate is a flat list of MATERIAL and LABOR line items in the OrderConfirmation shape
// (pulled in via the shared PricingContract "Add Scope" modal). These pure adapters map between
// that in-memory model ({ description, materialList, laborList }) and the save/load API shape,
// reusing existing entity ids as persistence keys (contactInfoId, storeId, unitTypeId/variantId).
// CnCMeasureQuantityId present => update existing estimate; omitted => create new.

import { recalcLinePrice, estimateItemsTotal } from "./manualEstimateItems";

const orZero = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const str = (v) => (v === null || v === undefined ? "" : String(v));

// Read a scope# tolerating the API's casing (.NET may echo PascalCase `ScopeBlockId`).
// Returns null when absent/blank so callers can tell "no scope#" from "scope# 0".
const readScopeBlockId = (item) => {
  const v = item?.scopeBlockId ?? item?.ScopeBlockId;
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Serialize line items for the payload: roomName and photoUrl live in nameList (one entry per
// scope block + product structure) so base64 photos are not duplicated across every row. Every row
// carries its scope# (scopeBlockId, default 0) so two scopes of the same product structure stay
// separate on reload — the API must persist this scope# per row.
const serializeItems = (list) =>
  (Array.isArray(list) ? list : []).map((item) => {
    // eslint-disable-next-line no-unused-vars
    const { roomName, photoUrl, ...rest } = item;
    return { ...recalcLinePrice(rest), scopeBlockId: orZero(item.scopeBlockId) };
  });

// A scope is uniquely identified by its scope# (scopeBlockId) + product structure. scopeBlockId 0
// identifies the original section of estimates saved before scope blocks were tracked; new blocks
// receive positive numbers, so adding a scope never overwrites that legacy section's metadata.
export const scopeMetadataKey = (item) =>
  `${orZero(item.scopeBlockId)}:${orZero(item.productStructureId ?? item.ProductStructureId)}`;

// Build the nameList: one entry per scope (keyed by scope# + product structure), carrying the
// room name and photo. scopeBlockId is ALWAYS emitted (the scope# every material/labor row
// references) so the API can round-trip repeated same-structure scopes as separate blocks.
export const buildScopeNameList = (rawList) => {
  const seen = new Map();
  rawList.forEach((item) => {
    const psid = orZero(item.productStructureId);
    const key = scopeMetadataKey(item);
    if (psid && !seen.has(key)) {
      seen.set(key, {
        scopeBlockId: orZero(item.scopeBlockId),
        productStructureId: psid,
        roomName: str(item.roomName),
        photoUrl: str(item.photoUrl),
      });
    }
  });
  return Array.from(seen.values());
};

// Build the request payload sent to SaveCountertopCabinetEstimate.
export function buildEstimatePayload(estimate = {}, identifiers = {}) {
  const { description = "", materialList = [], laborList = [], scopeName = "", scopePhotoUrl = "" } = estimate;
  const material = serializeItems(materialList);
  const labor = serializeItems(laborList);
  const nameList = buildScopeNameList([...materialList, ...laborList]);

  const cnCMeasureQuantityId = orZero(identifiers.cnCMeasureQuantityId);
  return {
    ...(cnCMeasureQuantityId > 0 ? { CnCMeasureQuantityId: cnCMeasureQuantityId } : {}),
    contactInfoId: orZero(identifiers.contactInfoId),
    storeId: orZero(identifiers.storeId),
    unitTypeId: orZero(identifiers.unitTypeId),
    isCountertopCabinet: true,
    description: str(description).trim(),
    scopeName: str(scopeName).trim(),
    // Estimate-level photo (next to "Estimate Name"): a saved URL, or a base64 data URL for a newly
    // picked image — sent the same way as the per-scope room photos in nameList.
    scopePhotoUrl: str(scopePhotoUrl),
    nameList,
    total: estimateItemsTotal(material, labor),
    material,
    labor,
  };
}

// Backfill the "belongs to which primary" label on kit (add-on) rows of a loaded estimate that
// predate the parentProductId feature (or whose name wasn't persisted by the backend). A row that
// already carries a parentProductName is left alone; otherwise its primary is resolved by
// parentProductId, or — when it carries none — by the primary sharing its kitId. For older
// shared-kit rows, a unique match to the saved EA/LF quantity supplies a display fallback.
// Tied or unmatched quantities stay blank. Primary rows
// live in the material list, so labor add-ons resolve their primary from there too. Pure.
export function backfillKitParentLabels(materialList = [], laborList = [], laborCatalog = []) {
  const mats = Array.isArray(materialList) ? materialList : [];
  const labs = Array.isArray(laborList) ? laborList : [];
  // Older API models discarded isPrimary and the parent fields. buildScopeItems gives
  // kit materials a positive materialId; a product with materialId 0 is a primary.
  // Only use that legacy distinction when the explicit flag is absent.
  const primaries = mats.filter((item) => item && (
    item.isPrimary === true ||
    (item.isPrimary == null && item.materialId != null &&
      orZero(item.materialId) === 0 && orZero(item.productId) > 0 &&
      !orZero(item.parentProductId) && !str(item.parentProductName).trim())
  ));
  const sameSection = (parent, item) =>
    (item.scopeBlockId == null || str(parent.scopeBlockId) === str(item.scopeBlockId)) &&
    // An explicit kit parent may belong to a different structure within the same block.
    (orZero(item.parentProductId) > 0 || !orZero(item.productStructureId) ||
      orZero(parent.productStructureId) === orZero(item.productStructureId));
  const unitOf = (item) => str(item.unit || item.unitName).trim().toUpperCase();
  const label = (item, isLabor = false) => {
    if (!item || primaries.includes(item) || item.isPrimary) return item;
    if (str(item.parentProductName).trim()) return item; // already labeled
    const catalogMatches = isLabor ? laborCatalog.filter((entry) =>
      orZero(entry.productStructureId) === orZero(item.productStructureId) &&
      (orZero(item.laborId) > 0
        ? orZero(entry.laborId) === orZero(item.laborId)
        : orZero(item.laborItemId) > 0 &&
          orZero(entry.laborItemId) === orZero(item.laborItemId))) : [];
    let candidates = primaries.filter((parent) => sameSection(parent, item));
    if (orZero(item.parentProductId) > 0) {
      candidates = candidates.filter((parent) =>
        orZero(parent.productId) === orZero(item.parentProductId));
    } else if (orZero(item.kitId) > 0) {
      candidates = candidates.filter((parent) => orZero(parent.kitId) === orZero(item.kitId));
    } else if (isLabor && orZero(item.productStructureId)) {
      // The saved API omits labor kitId, but the pricing-contract API still maps
      // laborId (the kit membership ID) to its kit. laborItemId is only a fallback:
      // the same labor item may occur in several different kits.
      if (catalogMatches.length) {
        const kitIds = new Set(catalogMatches.map((entry) => orZero(entry.kitId)).filter(Boolean));
        candidates = candidates.filter((parent) => kitIds.has(orZero(parent.kitId)));
      }
    } else {
      return item;
    }
    // Older shared-kit rows lost their per-primary link. EA masters copy their qty
    // to addons; LF masters store the rounded length and divide EA addons by coverage.
    // Use that saved relationship only within ONE kit and only for a unique positive
    // match. Do not use row order, zero/blank quantities, or override an explicit link.
    if (candidates.length > 1 && !orZero(item.parentProductId) &&
      orZero(item.quantity) > 0 &&
      candidates.every((parent) => ["EA", "LF"].includes(unitOf(parent))) &&
      orZero(candidates[0].kitId) > 0 &&
      candidates.every((parent) => orZero(parent.kitId) === orZero(candidates[0].kitId))) {
      const preFiltCandidates = candidates;
      const coverageValues = [...new Set(catalogMatches
        .filter((entry) => orZero(entry.kitId) === orZero(candidates[0].kitId))
        .map((entry) => orZero(entry.coverage)))];
      const coverage = orZero(item.coverage ?? (coverageValues.length === 1 ? coverageValues[0] : 0));
      candidates = candidates.filter((parent) => {
        const qty = orZero(parent.quantity);
        const expected = unitOf(parent) === "LF" && unitOf(item) === "EA" && coverage > 0
          ? Math.ceil(qty / coverage) : qty;
        return qty > 0 && Math.abs(expected - orZero(item.quantity)) < 0.000001;
      });
      // Shared-kit multi-primary: the labor qty was saved as the sum of all primaries, so no
      // single primary matches. When the saved qty equals the combined total, link to the first
      // primary as the canonical anchor so syncKitAddonQuantity can find this row.
      if (candidates.length === 0) {
        const sumQty = preFiltCandidates.reduce((s, p) => s + orZero(p.quantity), 0);
        if (sumQty > 0 && Math.abs(sumQty - orZero(item.quantity)) < 0.000001) {
          candidates = preFiltCandidates.slice(0, 1);
        }
      }
    }
    if (candidates.length !== 1) return item;
    const parent = candidates[0];
    return {
      ...item,
      parentProductId: orZero(parent.productId),
      parentProductName: str(parent.privateName || parent.productName),
    };
  };
  return {
    materialList: mats.map((item) => label(item)),
    laborList: labs.map((item) => label(item, true)),
  };
}

// Rebuild each row's scope# (scopeBlockId) from the nameList when the backend did not persist it on
// the material/labor rows. The nameList DOES round-trip (it carries each scope's room name/photo) and
// lists the scope blocks per product structure in save order; the rows also come back in save order,
// and every kit addon carries parentProductId linking it to its scope's primary product. So we can
// split each structure's rows back into the right scope blocks.
//
// Strictly safe: a split is only applied for a structure when it is UNAMBIGUOUS — the number of
// primary-product clusters matches the number of nameList scope blocks, and every labor row maps to
// exactly one primary. If anything is ambiguous (e.g. two scopes share the same primary product), that
// structure's rows are left untouched (they group together, the prior behavior) rather than risk
// assigning a row to the wrong scope. No-op when the backend already returns scopeBlockId on any row.
export function reconstructScopeBlocks(materialList = [], laborList = [], nameList = []) {
  const mats = Array.isArray(materialList) ? materialList : [];
  const labs = Array.isArray(laborList) ? laborList : [];

  // Backend already persisted the scope# on a row → trust it, nothing to rebuild.
  if ([...mats, ...labs].some((i) => readScopeBlockId(i) != null)) {
    return { materialList: mats, laborList: labs };
  }

  // Scope blocks per psid from the nameList, in order.
  const blocksByPsid = {};
  (Array.isArray(nameList) ? nameList : []).forEach((entry) => {
    const psid = orZero(entry.productStructureId);
    if (!psid) return;
    const sbid = readScopeBlockId(entry);
    (blocksByPsid[psid] = blocksByPsid[psid] || []).push(sbid == null ? 0 : sbid);
  });

  // Always re-stamp scopeBlockId from the nameList (not only when splitting): a scope block can span
  // several product structures (e.g. a Cabinet Doors item added into a Thermofoil scope), so each
  // structure's rows must carry their block's scope# to stay grouped together after reload — even
  // when every structure maps to a single block. Nothing in the nameList → leave rows untouched.
  if (Object.keys(blocksByPsid).length === 0) {
    return { materialList: mats, laborList: labs };
  }

  const byPsid = (list) => {
    const map = {};
    list.forEach((item, idx) => {
      const psid = orZero(item.productStructureId);
      (map[psid] = map[psid] || []).push({ item, idx });
    });
    return map;
  };
  const matByPsid = byPsid(mats);
  const labByPsid = byPsid(labs);

  const matSbid = new Map();
  const labSbid = new Map();

  // Global primary product → scope# map, used to recover CROSS-STRUCTURE added items (e.g. a Fixtures
  // row added into a Countertops scope): those rows live in a structure with no primary of its own, so
  // the per-structure clustering below can't place them, but each carries parentProductId = its block's
  // primary. A productId seen in two different blocks is ambiguous and dropped (never guess).
  const primaryToSbidGlobal = new Map();
  const ambiguousPrimaries = new Set();
  const recordPrimary = (pidRaw, sbid) => {
    const pid = orZero(pidRaw);
    if (!pid || ambiguousPrimaries.has(pid)) return;
    if (primaryToSbidGlobal.has(pid) && primaryToSbidGlobal.get(pid) !== sbid) {
      ambiguousPrimaries.add(pid);
      primaryToSbidGlobal.delete(pid);
      return;
    }
    primaryToSbidGlobal.set(pid, sbid);
  };

  Object.keys(blocksByPsid).forEach((psidStr) => {
    const psid = Number(psidStr);
    const blocks = blocksByPsid[psid];
    const matEntries = matByPsid[psid] || [];
    const labEntries = labByPsid[psid] || [];

    if (blocks.length < 2) {
      // Single scope for this structure: stamp every row with that one scope#.
      matEntries.forEach((e) => {
        matSbid.set(e.idx, blocks[0]);
        if (e.item.isPrimary === true) recordPrimary(e.item.productId, blocks[0]);
      });
      labEntries.forEach((e) => labSbid.set(e.idx, blocks[0]));
      return;
    }

    // Cluster material by primary boundary: a new scope begins at a primary row that follows at
    // least one addon of the current scope (buildScopeItems emits [primaries…, addons…] per block).
    const clusters = [];
    let current = null;
    let sawAddon = false;
    matEntries.forEach((e) => {
      const isPrimary = e.item.isPrimary === true;
      if (isPrimary && current && sawAddon) {
        clusters.push(current);
        current = null;
        sawAddon = false;
      }
      if (!current) current = [];
      current.push(e);
      if (!isPrimary) sawAddon = true;
    });
    if (current) clusters.push(current);

    // Only split when clusters line up 1:1 with the nameList scope blocks.
    if (clusters.length !== blocks.length) return; // ambiguous → leave this structure merged

    const tMat = new Map();
    const primaryToSbid = new Map();
    clusters.forEach((cluster, bIdx) => {
      const sbid = blocks[bIdx];
      cluster.forEach((e) => {
        tMat.set(e.idx, sbid);
        if (e.item.isPrimary === true) primaryToSbid.set(orZero(e.item.productId), sbid);
      });
    });

    // Labor rows attach to a scope via their parentProductId. Bail this structure if any labor row
    // can't be mapped to exactly one primary (e.g. two scopes reuse the same primary product).
    const tLab = new Map();
    let labOk = true;
    labEntries.forEach((e) => {
      const parentId = orZero(e.item.parentProductId);
      if (parentId > 0 && primaryToSbid.has(parentId)) tLab.set(e.idx, primaryToSbid.get(parentId));
      else labOk = false;
    });
    if (!labOk) return; // ambiguous labor → leave this structure merged

    tMat.forEach((v, k) => matSbid.set(k, v));
    tLab.forEach((v, k) => labSbid.set(k, v));
    // This structure split cleanly → its primaries can anchor added rows of OTHER structures. Record
    // from the clusters (not the local primaryToSbid map, which collapses a productId reused across
    // blocks) so a primary seen in two blocks is correctly flagged ambiguous and never anchors.
    clusters.forEach((cluster, bIdx) => {
      cluster.forEach((e) => {
        if (e.item.isPrimary === true) recordPrimary(e.item.productId, blocks[bIdx]);
      });
    });
  });

  // Recovery pass: any still-unassigned, non-primary row whose parentProductId points at a known
  // (unambiguous) primary inherits that primary's scope# — this is what reunites cross-structure
  // added items with their block when the backend dropped the per-row scope#.
  const recoverByParent = (list, assigned) => {
    list.forEach((item, idx) => {
      if (assigned.has(idx) || item.isPrimary === true) return;
      const parent = orZero(item.parentProductId);
      if (parent > 0 && primaryToSbidGlobal.has(parent)) assigned.set(idx, primaryToSbidGlobal.get(parent));
    });
  };
  recoverByParent(mats, matSbid);
  recoverByParent(labs, labSbid);

  return {
    materialList: mats.map((item, idx) => (matSbid.has(idx) ? { ...item, scopeBlockId: matSbid.get(idx) } : item)),
    laborList: labs.map((item, idx) => (labSbid.has(idx) ? { ...item, scopeBlockId: labSbid.get(idx) } : item)),
  };
}

// Rehydrate the frontend model from a saved estimate response.
// Restores roomName and photoUrl onto each item from nameList, keyed by scope# + product structure.
export function hydrateEstimate(saved, laborCatalog = []) {
  const source = saved?.result ? saved.result : saved;
  if (!source) return { estimateId: 0, description: "", scopePhotoUrl: "", materialList: [], laborList: [] };

  // Normalize the scope# casing (.NET may return PascalCase) onto a lowercase scopeBlockId so
  // grouping/lookup is casing-agnostic.
  const normSbid = (item) => {
    const sbid = readScopeBlockId(item);
    if (!item || typeof item !== "object") return item;
    // eslint-disable-next-line no-unused-vars
    const { ScopeBlockId, ...rest } = item;
    return sbid != null ? { ...rest, scopeBlockId: sbid } : rest;
  };

  const rawMaterial = (Array.isArray(source.material) ? source.material : []).map(normSbid);
  const rawLabor = (Array.isArray(source.labor) ? source.labor : []).map(normSbid);
  const rawNameList = (Array.isArray(source.nameList) ? source.nameList : []).map((e) => ({
    ...e,
    scopeBlockId: readScopeBlockId(e) ?? orZero(e.scopeBlockId),
  }));

  // If the backend dropped the per-row scope#, rebuild it from the nameList so repeated
  // same-structure scopes stay separate on reload. No-op when rows already carry scopeBlockId.
  const rebuilt = reconstructScopeBlocks(rawMaterial, rawLabor, rawNameList);

  // Room name / photo per scope, keyed by scope# + product structure (exact). A scope BLOCK can span
  // several product structures (items added via "Add New Item"), and room/photo are block-level, so
  // also build a scope#-only map: any item in the block inherits the block's room/photo even if its
  // own (scope# + structure) entry is missing. Legacy psid-only key kept for pre-scope-block saves.
  //
  // Block-level inheritance is ONLY for real scope blocks (positive scope#). Block 0 is the legacy
  // catch-all bucket that may hold several genuinely different scopes/structures, so inheriting its
  // "first room" would mis-assign a room; leave block-0 items to their exact/psid-only match instead.
  const nameMap = {};
  const nameMapByBlock = {};
  rawNameList.forEach((entry) => {
    const psid = orZero(entry.productStructureId);
    if (!psid) return;
    const meta = { roomName: str(entry.roomName), photoUrl: str(entry.photoUrl) };
    const key = scopeMetadataKey(entry);
    if (!nameMap[key]) nameMap[key] = meta;
    const sbid = orZero(entry.scopeBlockId);
    if (sbid > 0 && !nameMapByBlock[sbid] && (meta.roomName || meta.photoUrl)) nameMapByBlock[sbid] = meta;
    const legacyKey = scopeMetadataKey({ productStructureId: psid });
    if (!nameMap[legacyKey]) nameMap[legacyKey] = meta;
  });

  const scopeName = str(source.scopeName);
  const restoreItem = (item) => {
    const meta =
      nameMap[scopeMetadataKey(item)] ||
      nameMapByBlock[orZero(item.scopeBlockId)] ||
      nameMap[scopeMetadataKey({ productStructureId: item.productStructureId })] ||
      {};
    return {
      ...item,
      scopeName,
      roomName: meta.roomName ?? item.roomName ?? "",
      photoUrl: meta.photoUrl ?? item.photoUrl ?? "",
    };
  };

  const materialList = rebuilt.materialList.map(restoreItem);
  const laborList = rebuilt.laborList.map(restoreItem);
  // Ensure kit rows of existing/loaded estimates show which primary they belong to.
  const labeled = backfillKitParentLabels(materialList, laborList, laborCatalog);

  return {
    estimateId: orZero(source.estimateId),
    cnCMeasureQuantityId: orZero(source.cnCMeasureQuantityId),
    scopeName,
    scopePhotoUrl: str(source.scopePhotoUrl),
    description: str(source.description),
    materialList: labeled.materialList,
    laborList: labeled.laborList,
  };
}
